import { useRef } from "react";
import { Languages, RotateCcw, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { GuildConfig } from "@/lib/api";
import { Section } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { groupRegistry, I18N_REGISTRY } from "@/lib/i18nRegistry";

type Props = {
  config: GuildConfig;
  onChange: (v: GuildConfig["language"]) => void;
};

export function LanguageTab({ config, onChange }: Props) {
  const cfg = config.language;
  const overrides = cfg.overrides || {};
  const groups = groupRegistry();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setOverride = (key: string, value: string) => {
    const next = { ...overrides };
    if (value.trim()) {
      next[key] = value;
    } else {
      delete next[key];
    }
    onChange({ ...cfg, overrides: next });
  };

  const resetOne = (key: string) => setOverride(key, "");

  // Simple "Label = "value"" text format, grouped under section comments — easy to
  // hand a translator or edit directly. Escape backslash/quote so a value containing
  // either doesn't break the line.
  const escapeLine = (s: string) => s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\r?\n/g, " ");
  const unescapeLine = (s: string) => s.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  const lineRe = /^(.+?)\s*=\s*"((?:\\.|[^"\\])*)"\s*$/;

  const handleExport = () => {
    const lines: string[] = [];
    for (const [groupName, entries] of Object.entries(groups)) {
      lines.push(`# ${groupName}`);
      for (const entry of entries) {
        lines.push(`${escapeLine(entry.default)} = "${escapeLine(overrides[entry.key] || "")}"`);
      }
      lines.push("");
    }
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "areon-translation.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    // Multiple registry keys can share the exact same English default (e.g. "Channel"
    // in both Logging and Security) — group by default text so one translated line
    // fills in every key that uses that exact wording.
    const byDefault = new Map<string, string[]>();
    for (const entry of I18N_REGISTRY) {
      const list = byDefault.get(entry.default);
      if (list) list.push(entry.key);
      else byDefault.set(entry.default, [entry.key]);
    }

    const next = { ...overrides };
    let imported = 0;
    let skipped = 0;
    for (const rawLine of text.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line || line.startsWith("#")) continue;
      const m = lineRe.exec(line);
      if (!m) {
        skipped++;
        continue;
      }
      const label = m[1].trim();
      const value = unescapeLine(m[2]);
      const keys = byDefault.get(label);
      if (!keys) {
        skipped++;
        continue;
      }
      for (const key of keys) {
        if (value.trim()) next[key] = value;
        else delete next[key];
      }
      imported++;
    }

    if (imported === 0) {
      toast.error("No matching translation lines found in that file.");
      return;
    }
    onChange({ ...cfg, overrides: next });
    toast.success(`Imported ${imported} translation${imported === 1 ? "" : "s"}${skipped ? ` (${skipped} line${skipped === 1 ? "" : "s"} skipped)` : ""}.`);
  };

  return (
    <Section
      icon={Languages}
      title="Language"
      description="Translate the bot's own wording (titles, field labels, buttons) into your server's language. Anything left blank stays in English — new strings from future updates default to English too, until translated here."
      enabled={cfg.enabled}
      onToggle={(v) => onChange({ ...cfg, enabled: v })}
      actions={
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,text/plain"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              e.target.value = "";
            }}
          />
          <Button size="sm" variant="outline" onClick={handleImportClick} className="gap-1.5">
            <Upload className="h-3.5 w-3.5" /> Import
          </Button>
          <Button size="sm" variant="outline" onClick={handleExport} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> Export
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          This only relabels the bot's own UI text (like "Role Updated" or "Moderator") — it does <strong>not</strong>{" "}
          translate user-written content like welcome messages, embeds, or custom commands, since you already write
          those yourself in whatever language you want. Coverage grows over time — more modules will appear here as
          they're wired up. Where a field shows <code className="text-[11px] bg-muted px-1 rounded">{"{placeholders}"}</code>{" "}
          like <code className="text-[11px] bg-muted px-1 rounded">{"{user}"}</code>, keep them somewhere in your
          translation — that's where the bot inserts the real name/count/etc. A translation missing one is ignored
          and falls back to English so nothing ever breaks.
        </p>
        <p>
          Use <strong>Export</strong> to download a plain text file, one line per field —{" "}
          <code className="text-[11px] bg-muted px-1 rounded">Member Joined = ""</code> — grouped by section. Fill in
          the quotes with your translation and hit <strong>Import</strong> to load it back in; blank lines and lines
          that don't match a known label are skipped safely.
        </p>
      </div>

      {Object.entries(groups).map(([groupName, entries]) => (
        <Card key={groupName} className="p-3 sm:p-4 space-y-4 border-border/50 min-w-0 overflow-hidden">
          <div className="font-medium">{groupName}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {entries.map((entry) => {
              const value = overrides[entry.key] || "";
              const isOverridden = value.trim().length > 0;
              const tokens = [...entry.default.matchAll(/\{(\w+)\}/g)].map((m) => m[0]);
              const missingTokens = tokens.filter((tok) => !value.includes(tok));
              const hasWarning = isOverridden && missingTokens.length > 0;
              return (
                <div key={entry.key} className="space-y-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <label className="text-xs font-medium text-muted-foreground break-words" title={entry.key}>
                      {entry.default}
                    </label>
                    {isOverridden && (
                      <button
                        type="button"
                        onClick={() => resetOne(entry.key)}
                        className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0 py-1 -my-1 px-1"
                        title="Reset to default"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Reset
                      </button>
                    )}
                  </div>
                  <Input
                    value={value}
                    placeholder={entry.default}
                    onChange={(e) => setOverride(entry.key, e.target.value)}
                    className={`text-base sm:text-sm ${hasWarning ? "border-amber-500/60 focus-visible:ring-amber-500/40" : ""}`}
                  />
                  {tokens.length > 0 && (
                    <div className={`text-[10px] break-words ${hasWarning ? "text-amber-500" : "text-muted-foreground/70"}`}>
                      {hasWarning
                        ? `⚠ Missing ${missingTokens.join(", ")} — will fall back to English until added.`
                        : `Placeholders: ${tokens.join(", ")}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </Section>
  );
}
